import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  FormControlLabel,
  Switch,
  Grid,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../context";
import useFetchObjects from "../../../../../api/useFetchObjects";
import useAdd from "../../../../../api/useAdd";
import { useQueryClient } from "@tanstack/react-query";

const UltrasoundDialog = ({
  open,
  onClose,
  patientId,
  doctorId,
  ticketId = null,
}) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const queryClient = useQueryClient();

  const { data: ultrasoundTypesData } = useFetchObjects(
    ["ultrasound-types"],
    "ultrasound-types/",
    token,
  );

  const { handleAdd, loading, resetForm } = useAdd(
    "ultrasounds",
    token,
    t("profile.ultrasoundAdded") || "Ultrasound order created successfully",
    t("profile.ultrasoundAddFailed") || "Failed to create Ultrasound order",
  );

  const [formData, setFormData] = useState({
    ultrasound_type: null,
    date: new Date().toISOString().split("T")[0],
    is_urgent: false,
    total_amount: 0,
  });
  const [errors, setErrors] = useState({});

  const ultrasoundTypes =
    ultrasoundTypesData?.results || ultrasoundTypesData || [];

  useEffect(() => {
    if (resetForm) {
      setFormData({
        ultrasound_type: null,
        date: new Date().toISOString().split("T")[0],
        is_urgent: false,
        total_amount: 0,
      });
      setErrors({});
      onClose();
      queryClient.invalidateQueries(["unified-ehr", patientId]);
    }
  }, [resetForm, onClose, queryClient, patientId]);

  const handleUltrasoundTypeChange = (event, newValue) => {
    setFormData({
      ...formData,
      ultrasound_type: newValue?.id || null,
      total_amount: newValue?.cost || 0,
    });
    if (errors.ultrasound_type) {
      setErrors({ ...errors, ultrasound_type: "" });
    }
  };

  const handleSubmit = () => {
    const newErrors = {};
    if (!formData.ultrasound_type) {
      newErrors.ultrasound_type =
        t("profile.ultrasoundTypeRequired") || "Ultrasound type is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const submitData = {
      patient: patientId,
      doctor: doctorId,
      ticket: ticketId || null,
      ultrasound_type: formData.ultrasound_type,
      date: formData.date,
      is_urgent: formData.is_urgent,
      total_amount: formData.total_amount,
    };

    handleAdd(submitData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t("profile.addUltrasound") || "Add Ultrasound Order"}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Autocomplete
              options={ultrasoundTypes}
              getOptionLabel={(option) => option.name || ""}
              value={
                ultrasoundTypes.find(
                  (t) => t.id === formData.ultrasound_type,
                ) || null
              }
              onChange={handleUltrasoundTypeChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("profile.ultrasoundType") || "Ultrasound Type"}
                  error={!!errors.ultrasound_type}
                  helperText={errors.ultrasound_type}
                  required
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("profile.date") || "Date"}
              type="date"
              value={formData.date}
              onChange={(e) =>
                setFormData({ ...formData, date: e.target.value })
              }
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("profile.totalAmount") || "Total Amount"}
              type="number"
              value={formData.total_amount}
              onChange={(e) =>
                setFormData({ ...formData, total_amount: e.target.value })
              }
              size="small"
              InputProps={{
                readOnly: formData.ultrasound_type !== null,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_urgent}
                  onChange={(e) =>
                    setFormData({ ...formData, is_urgent: e.target.checked })
                  }
                />
              }
              label={t("profile.urgent") || "Urgent"}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t("profile.cancel") || "Cancel"}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading
            ? t("profile.saving") || "Saving..."
            : t("profile.add") || "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UltrasoundDialog;
